import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { preview, getImageUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Lock, UserPlus, ShieldAlert, Eye, LogIn, X, Filter, Menu, Sparkles, Clock } from 'lucide-react';

const DEVICE_ID_KEY = 'preview_device_id';

function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

// City options - matches main site ProductsPage.jsx
const cityOptions = [
  { value: 'australia', label: 'Australia' },
  { value: 'melbourne', label: 'Melbourne' },
  { value: 'sydney', label: 'Sydney' },
  { value: 'brisbane', label: 'Brisbane' },
  { value: 'adelaide', label: 'Adelaide' },
  { value: 'perth', label: 'Perth' },
  { value: 'cairns', label: 'Cairns' },
  { value: 'gold coast', label: 'Gold Coast' },
  { value: 'darwin', label: 'Darwin' },
  { value: 'tasmania', label: 'Tasmania' },
  { value: 'bondi', label: 'Bondi' },
];

const PreviewProductCard = ({ product }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageUrl = getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400';

  return (
    <div className="group block" data-testid={`preview-product-card-${product.id}`}>
      <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-4 relative">
        {!imageLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
        <img
          src={imageUrl}
          alt={product.name}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400'; setImageLoaded(true); }}
        />
        {product.is_coming_soon && (
          <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 to-transparent flex flex-col justify-end p-4 z-10">
            <Badge className="bg-blue-600 text-white text-xs px-2 py-1 font-bold w-fit">
              <Clock className="w-3 h-3 mr-1" />
              COMING SOON
            </Badge>
          </div>
        )}
        {product.is_new && !product.is_coming_soon && (
          <div className="absolute top-2 left-2 z-10">
            <Badge className="bg-green-500 text-white text-[10px] px-2 py-1 font-bold">
              <Sparkles className="w-3 h-3 mr-1" />
              NEW
            </Badge>
          </div>
        )}
        {product.is_best_seller && !product.is_coming_soon && (
          <div className="absolute bottom-2 left-2 z-10">
            <Badge className="bg-yellow-500 text-black text-[9px] px-1.5 py-0.5 font-bold">BEST SELLER</Badge>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-[#0F172A] line-clamp-2">{product.name}</h3>
        <p className="text-xs text-[#64748B] font-mono">{product.sku}</p>
        <div className="flex items-center gap-1 text-[#64748B] pt-1">
          <Lock className="w-3 h-3" />
          <span className="text-sm">Sign up for pricing</span>
        </div>
      </div>
    </div>
  );
};

const PreviewPage = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const deviceId = getOrCreateDeviceId();

  useEffect(() => {
    const claimLink = async () => {
      try {
        await preview.claim(token, deviceId);
        setStatus('valid');
      } catch (err) {
        const code = err.response?.status;
        if (code === 403) {
          setStatus('device_locked');
          setErrorMsg('This preview link has already been used on another device. Please request a new link.');
        } else {
          setStatus('invalid');
          setErrorMsg(err.response?.data?.detail || 'This preview link is invalid or has been revoked.');
        }
      }
    };
    claimLink();
  }, [token, deviceId]);

  useEffect(() => {
    if (status !== 'valid') return;
    preview.getCategories(token, deviceId).then(res => setCategories(res.data)).catch(() => {});
  }, [status, token, deviceId]);

  // Shorten category names for the nav bar
  const shortName = (name) => {
    return name
      .replace(/ Unisex$/i, '')
      .replace(/^Tote Bags$/i, 'Bags')
      .replace(/^Kids Clothing$/i, 'Kids')
      .replace(/^T-Shirt Lady Fit$/i, 'Lady Fit')
      .replace(/^Zip Hoodie$/i, 'Zipper');
  };

  // Fixed display order - new categories appear after Zipper
  const categoryOrder = [
    't-shirts', 'Tshirt Ladyfit', 'hoodies', 'sweatshirts', 'zip hoodie',
    'caps', 'bags', 'boxers', 'shorts', 'hotpants', 'kids'
  ];

  const sortCategories = (cats) => {
    const known = [];
    const unknown = [];
    for (const cat of cats) {
      const idx = categoryOrder.indexOf(cat.slug);
      if (idx !== -1) {
        known.push({ ...cat, _order: idx });
      } else {
        unknown.push(cat);
      }
    }
    known.sort((a, b) => a._order - b._order);
    const zipperIdx = known.findIndex(c => c.slug === 'zip hoodie');
    const insertAt = zipperIdx !== -1 ? zipperIdx + 1 : known.length;
    return [...known.slice(0, insertAt), ...unknown, ...known.slice(insertAt)];
  };

  const sortedCategories = sortCategories(categories);

  const fetchProducts = useCallback(async () => {
    if (status !== 'valid') return;
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (selectedCategory) params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;
      const res = await preview.getProducts(token, deviceId, params);
      setProducts(res.data.products);
      setTotal(res.data.total);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [status, token, deviceId, selectedCategory, searchTerm]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Client-side city filtering (same as ProductsPage)
  const filteredProducts = selectedCity
    ? products.filter(p =>
        p.name.toLowerCase().startsWith(selectedCity.toLowerCase()) ||
        p.name.toLowerCase().includes(selectedCity.toLowerCase())
      )
    : products;

  const handleCategoryChange = (value) => {
    setSelectedCategory(value === 'all' ? '' : value);
  };

  const handleCityChange = (value) => {
    setSelectedCity(value === 'all' ? '' : value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedCity('');
  };

  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat?.name || categoryLinks.find(c => c.slug === slug)?.name || slug;
  };

  const getCityName = (value) => {
    const city = cityOptions.find(c => c.value === value);
    return city?.label || value;
  };

  // Error states
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-pulse text-[#64748B]">Verifying preview link...</div>
      </div>
    );
  }

  if (status === 'invalid' || status === 'device_locked') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <div className="max-w-md w-full text-center space-y-6" data-testid="preview-error">
          <ShieldAlert className="w-16 h-16 text-red-400 mx-auto" />
          <h1 className="text-2xl font-bold text-[#0F172A]">Access Denied</h1>
          <p className="text-[#64748B]">{errorMsg}</p>
          <div className="flex flex-col gap-3">
            <Link to="/login">
              <Button className="w-full bg-[#002FA7] hover:bg-[#0025a0]" data-testid="preview-error-login-btn">
                <LogIn className="w-4 h-4 mr-2" /> Log In
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="w-full" data-testid="preview-error-signup-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Sign Up
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="preview-page">
      {/* Preview Banner */}
      <div className="bg-[#002FA7] text-white py-2 px-4 text-center" data-testid="preview-banner">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
          <Eye className="w-4 h-4 shrink-0" />
          <span className="text-xs sm:text-sm font-medium">
            Preview mode — Sign up to see prices and place orders
          </span>
          <Link to="/register">
            <Button size="sm" className="bg-white text-[#002FA7] hover:bg-gray-100 text-xs h-6 px-3 font-semibold" data-testid="preview-banner-signup-btn">
              Sign Up
            </Button>
          </Link>
        </div>
      </div>

      {/* Header - Matches main site */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="https://irishconnexxion.com.au/wp-content/uploads/2021/06/Asset-1@3x.png"
                alt="Irish Connexxion"
                className="h-10 md:h-12 w-auto"
              />
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <span className="text-sm font-medium text-[#0F172A]">Products</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-sm font-medium text-[#64748B] hover:text-[#002FA7] transition-colors flex items-center gap-1">
                    Categories
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {sortedCategories.map((cat) => (
                    <DropdownMenuItem key={cat.id || cat.slug} onClick={() => handleCategoryChange(cat.slug)} className="cursor-pointer">
                      {shortName(cat.name)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-sm font-medium" data-testid="preview-header-login">Login</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#002FA7] hover:bg-[#0025a0] text-white text-sm font-medium px-4 py-2" data-testid="preview-header-register">Register</Button>
              </Link>
              <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Category Bar - Desktop */}
          <div className="hidden md:block border-t border-[#E2E8F0] bg-[#F8FAFC] overflow-x-auto">
            <div className="flex items-center justify-center gap-1 py-2 whitespace-nowrap">
              <button
                onClick={() => handleCategoryChange('all')}
                className={`px-4 py-1.5 text-sm font-medium transition-colors rounded ${!selectedCategory ? 'text-[#002FA7] bg-white' : 'text-[#0F172A] hover:text-[#002FA7] hover:bg-white'}`}
              >
                All Products
              </button>
              <span className="text-[#E2E8F0]">|</span>
              {sortedCategories.map((cat, index) => (
                <React.Fragment key={cat.id || cat.slug}>
                  <button
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-4 py-1.5 text-sm transition-colors rounded ${selectedCategory === cat.slug ? 'text-[#002FA7] bg-white font-medium' : 'text-[#64748B] hover:text-[#002FA7] hover:bg-white'}`}
                  >
                    {shortName(cat.name)}
                  </button>
                  {index < sortedCategories.length - 1 && <span className="text-[#E2E8F0]">|</span>}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-[#E2E8F0] py-4">
              <nav className="flex flex-col gap-2">
                <button onClick={() => { handleCategoryChange('all'); setMobileMenuOpen(false); }} className="px-4 py-2 text-sm font-medium text-[#0F172A] text-left hover:bg-[#F1F5F9]">All Products</button>
                <div className="border-t border-[#E2E8F0] mt-2 pt-2">
                  <p className="px-4 py-1 text-xs font-semibold text-[#94A3B8] uppercase">Categories</p>
                  {sortedCategories.map((cat) => (
                    <button key={cat.id || cat.slug} onClick={() => { handleCategoryChange(cat.slug); setMobileMenuOpen(false); }} className="px-4 py-2 text-sm text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#002FA7] block w-full text-left">
                      {shortName(cat.name)}
                    </button>
                  ))}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-2">
              {selectedCity && selectedCategory
                ? `${getCityName(selectedCity)} - ${getCategoryName(selectedCategory)}`
                : selectedCity
                  ? `${getCityName(selectedCity)} Products`
                  : selectedCategory
                    ? getCategoryName(selectedCategory)
                    : 'All Products'}
            </h1>
            <p className="text-[#64748B]">
              {selectedCity ? `${filteredProducts.length} of ${total}` : total} products available
            </p>
          </div>

          {/* Filters - Matches main ProductsPage */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search products..."
                  className="pl-10 h-12"
                  data-testid="preview-search-input"
                />
              </div>
              <Button type="submit" className="h-12 px-6 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="preview-search-btn">
                Search
              </Button>
            </form>

            <div className="flex gap-2 flex-wrap">
              <Select value={selectedCity || 'all'} onValueChange={handleCityChange}>
                <SelectTrigger className="w-[150px] h-12" data-testid="preview-city-filter">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cityOptions.map((city) => (
                    <SelectItem key={city.value} value={city.value}>{city.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedCategory || 'all'} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[180px] h-12" data-testid="preview-category-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(selectedCategory || selectedCity || searchTerm) && (
                <Button variant="outline" onClick={clearFilters} className="h-12" data-testid="preview-clear-filters">
                  <X className="w-4 h-4 mr-2" /> Clear
                </Button>
              )}
            </div>
          </div>

          {/* Products Grid - Matches main site layout */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-[#F1F5F9] mb-4" />
                  <div className="h-4 bg-[#F1F5F9] mb-2 w-3/4" />
                  <div className="h-4 bg-[#F1F5F9] w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#64748B] text-lg">No products found</p>
              <Button onClick={clearFilters} variant="outline" className="mt-4">Clear Filters</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="preview-product-grid">
              {filteredProducts.map((product) => (
                <PreviewProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer - Matches main site */}
      <footer className="bg-[#0F172A] text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
            <div className="md:col-span-1">
              <img
                src="https://irishconnexxion.com.au/wp-content/uploads/2021/06/Asset-1@3x.png"
                alt="Irish Connexxion"
                className="h-10 w-auto brightness-0 invert mb-4"
              />
              <p className="text-sm text-[#94A3B8] leading-relaxed">
                Premium Wholesale Clothing Solutions for Travel Retail
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4 text-white">Products</h4>
              <ul className="space-y-2">
                {categories.slice(0, 5).map(cat => (
                  <li key={cat.id || cat.slug}>
                    <button onClick={() => { handleCategoryChange(cat.slug); window.scrollTo(0, 0); }} className="text-sm text-[#94A3B8] hover:text-white transition-colors">
                      {cat.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4 text-white">Account</h4>
              <ul className="space-y-2">
                <li><Link to="/login" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Login</Link></li>
                <li><Link to="/register" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Register</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4 text-white">Get Started</h4>
              <p className="text-sm text-[#94A3B8] mb-4">Create an account to access wholesale pricing and place orders.</p>
              <Link to="/register">
                <Button className="bg-[#002FA7] hover:bg-[#0025a0] text-sm" data-testid="preview-footer-signup">
                  <UserPlus className="w-4 h-4 mr-2" /> Sign Up Now
                </Button>
              </Link>
            </div>
          </div>
          <div className="border-t border-[#1E293B] mt-12 pt-8">
            <p className="text-sm text-[#64748B] text-center">
              &copy; 2026 Irish Connexxion. All rights reserved. B2B Wholesale Portal.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PreviewPage;
